import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ClientProfile } from '@/hooks/use-client-profile'
import { useAuth } from '@/shared/auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card'
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/shared/ui/form'
import { Input } from '@/shared/ui/input'

interface ConfirmationStepProps {
    form: ReturnType<typeof useForm<any>>
    clientProfile?: ClientProfile | null
    userIdFromUrl?: string | null
}

export function ClientDetails({ form, clientProfile, userIdFromUrl }: ConfirmationStepProps) {
    const { t } = useTranslation()
    const { user } = useAuth()

    // Lógica para determinar qué mostrar:
    // - Si hay dirección guardada en el perfil Y hay clientAddress lleno Y NO hay campos separados → campo único (no parseable)
    // - Si no hay dirección guardada O hay campos separados llenos → campos separados (usuario nuevo o parseado)
    const clientAddress = form.watch('clientAddress')
    const clientStreet = form.watch('clientStreet')
    const hasSavedAddress = !!(clientProfile?.address && clientAddress && !clientStreet)

    // No mostrar campos de contraseña si:
    // - El usuario es staff
    // - Hay un userId en la URL (el admin está agregando reserva para un cliente específico)
    const shouldShowPasswordFields = clientProfile && !clientProfile.hasPassword && !user?.isStaff && !userIdFromUrl

    const isEmailPasswordUser = user?.email && user?.providerData?.[0]?.providerId === 'password'
    const shouldHideEmailField = isEmailPasswordUser
    return (
        <Card>
            <CardContent>
                <div className='mt-4 space-y-4'>
                    <div
                        className={`grid grid-cols-1 gap-4 ${
                            shouldHideEmailField ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
                        }`}
                    >
                        <FormField
                            control={form.control}
                            name='clientName'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('booking.step4.clientName.label', 'Nombre')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder={t('booking.step4.clientName.placeholder', 'Tu nombre')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='clientLastName'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('booking.step4.clientLastName.label', 'Apellidos')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder={t('booking.step4.clientLastName.placeholder', 'Tus apellidos')}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {shouldHideEmailField && (
                            <FormField
                                control={form.control}
                                name='clientPhone'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('booking.step4.clientPhone.label', 'Teléfono con Whatsapp')}
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type='tel'
                                                placeholder={t('booking.step4.clientPhone.placeholder', '+34666777888')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                    </div>
                    {!shouldHideEmailField && (
                        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                            <FormField
                                control={form.control}
                                name='clientEmail'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('booking.step4.clientEmail.label', 'Email')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='email'
                                                placeholder={t('booking.step4.clientEmail.placeholder', 'tu@email.com')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='clientPhone'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('booking.step4.clientPhone.label', 'Teléfono con Whatsapp')}
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                type='tel'
                                                placeholder={t('booking.step4.clientPhone.placeholder', '+34666777888')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    )}
                    {hasSavedAddress ? (
                        <div className='grid grid-cols-1 gap-4'>
                            <FormField
                                control={form.control}
                                name='clientAddress'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('booking.step4.clientAddress.label', 'Dirección')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t(
                                                    'booking.step4.clientAddress.placeholder',
                                                    'Tu dirección completa',
                                                )}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    ) : (
                        <div className='grid grid-cols-1 gap-4'>
                            <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
                                <FormField
                                    control={form.control}
                                    name='clientStreet'
                                    render={({ field }) => (
                                        <FormItem className='sm:col-span-2'>
                                            <FormLabel>{t('booking.step4.clientStreet.label', 'Calle')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={t(
                                                        'booking.step4.clientStreet.placeholder',
                                                        'Nombre de la calle',
                                                    )}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='clientStreetNumber'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                {t('booking.step4.clientStreetNumber.label', 'Número')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={t(
                                                        'booking.step4.clientStreetNumber.placeholder',
                                                        'Número',
                                                    )}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                                <FormField
                                    control={form.control}
                                    name='clientCity'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t('booking.step4.clientCity.label', 'Ciudad')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={t('booking.step4.clientCity.placeholder', 'Ciudad')}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='clientPostalCode'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                {t('booking.step4.clientPostalCode.label', 'Código Postal')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={t(
                                                        'booking.step4.clientPostalCode.placeholder',
                                                        'Código Postal',
                                                    )}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                                <FormField
                                    control={form.control}
                                    name='clientProvince'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                {t('booking.step4.clientProvince.label', 'Provincia')}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={t(
                                                        'booking.step4.clientProvince.placeholder',
                                                        'Provincia',
                                                    )}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='clientCountry'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>{t('booking.step4.clientCountry.label', 'País')}</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder={t('booking.step4.clientCountry.placeholder', 'País')}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                    )}

                    {shouldShowPasswordFields && (
                        <Card className='mt-6 border-blue-200 bg-blue-50'>
                            <CardHeader>
                                <CardTitle className='text-lg'>
                                    {t('auth.existingClient.createPassword', 'Crear Contraseña')}
                                </CardTitle>
                                <CardDescription>
                                    {t(
                                        'auth.existingClient.createPasswordMessage',
                                        'Para futuras reservas, crea tu contraseña de acceso',
                                    )}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                                    <FormField
                                        control={form.control}
                                        name='newPassword'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    {t('auth.existingClient.password', 'Nueva Contraseña')}
                                                </FormLabel>
                                                <FormControl>
                                                    <Input type='password' {...field} />
                                                </FormControl>
                                                <FormDescription>
                                                    {t(
                                                        'auth.existingClient.passwordDescription',
                                                        'Mínimo 8 caracteres',
                                                    )}
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name='confirmPassword'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    {t('auth.existingClient.confirmPassword', 'Confirmar Contraseña')}
                                                </FormLabel>
                                                <FormControl>
                                                    <Input type='password' {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
